import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';

import { Filter } from '../Filter';
import { usePoiFilterOptions } from '../../map-data/use-filter-options';
import { useNavigationBarData } from '../NavigationBar/NavigationBarProvider';
import type { FilterConfig } from '../Filter/filter-types';
import { theme } from '../../styles/theme';
import { hexToRgb } from '../../utils/colour-utils';
import {
  poiFilterRegistry,
  buildYesNoOptions,
} from '../../map-data/poi-filter-registry';

export function PointOfInterestFilters() {
  const { selectedNavigationItem } = useNavigationBarData();
  const { t } = useTranslation();
  const isDesktop = useMediaQuery(
    `(min-width: ${theme.breakPoints.desktop}px)`,
  );
  const {
    resetFilterOptions,
    updateFilterOptions,
    filterConfig: poiFilterDataOptions,
  } = usePoiFilterOptions();

  const reg = poiFilterRegistry;

  const designFilterRow = {
    icon: reg.design.icon,
    title: t(reg.design.titleKey),
    key: reg.design.key,
    width: reg.design.width,
    clearValue: reg.design.clearValue,
    keyOptions: [
      {
        label: t('pinMap.studyDesigns.seeAll'),
        value: 'SeeAllDesigns',
        color: '255, 255, 255',
        textColor: '#000000',
      },
      {
        value: 'Clinical trial',
        label: t('pinMap.studyDesigns.clinicalTrial'),
        color: hexToRgb(theme.colors['blue-study']),
      },
      {
        value: 'Implementation study',
        label: t('pinMap.studyDesigns.implementationStudy'),
        color: hexToRgb(theme.colors['pale-orange']),
      },
      {
        value: 'Regional programme',
        label: t('pinMap.studyDesigns.regionalProgramme'),
        color: hexToRgb(theme.colors['yellow-100']),
      },
      {
        value: 'National programme',
        label: t('pinMap.studyDesigns.nationalProgramme'),
        color: hexToRgb(theme.colors['green-100']),
      },
    ],
    onUpdate: updateFilterOptions,
    filterType: 'multiChoiceToggle' as const,
  };

  const buildYesNoFilter = (key: keyof typeof poiFilterRegistry) => {
    const def = reg[key];
    const subtitleKey = 'subtitleKey' in def ? def.subtitleKey : undefined;
    return {
      icon: def.icon,
      title: t(def.titleKey),
      subtitle: subtitleKey ? t(subtitleKey) : undefined,
      key: def.key,
      keyOptions: buildYesNoOptions(t),
      onUpdate: updateFilterOptions,
      filterType: 'multiChoice' as const,
    };
  };

  const statusFilter = {
    icon: reg.status.icon,
    title: t(reg.status.titleKey),
    key: reg.status.key,
    keyOptions: [
      { value: undefined, label: t('poiExperience.filters.options.seeAll') },
      { value: 'Active', label: t('poiExperience.filters.options.active') },
      { value: 'Inactive', label: t('poiExperience.filters.options.inactive') },
    ],
    onUpdate: updateFilterOptions,
    filterType: 'multiChoice' as const,
  };

  const sexFilter = {
    icon: reg.sex.icon,
    title: t(reg.sex.titleKey),
    key: reg.sex.key,
    keyOptions: [
      { value: undefined, label: t('poiExperience.filters.options.seeAll') },
      { value: 'Both', label: t('poiExperience.filters.options.both') },
      { value: 'Male', label: t('poiExperience.filters.options.male') },
      { value: 'Female', label: t('poiExperience.filters.options.female') },
    ],
    onUpdate: updateFilterOptions,
    filterType: 'multiChoice' as const,
  };

  const dateRangeFilter = {
    icon: reg.dateRange.icon,
    title: t(reg.dateRange.titleKey),
    key: 'dateRange',
    rangeFrom: {
      min:
        poiFilterDataOptions?.dateRange.minFinish &&
        poiFilterDataOptions.dateRange.minFinish >= 1990
          ? poiFilterDataOptions.dateRange.minFinish
          : 1990,
      key: 'startFinish',
    },
    rangeTo: {
      max:
        poiFilterDataOptions?.dateRange.maxFinish ??
        new Date().getFullYear() + 25,
      key: 'endFinish',
    },
    onUpdate: (props: Record<string, string | undefined>) => {
      const { startFinish, endFinish } = props;
      const filters: Record<string, string | undefined> = {};
      const hasChangedStartDate =
        !!startFinish &&
        Number(startFinish) !== poiFilterDataOptions?.dateRange.minFinish;
      const hasChangedEndDate =
        !!endFinish &&
        Number(endFinish) !== poiFilterDataOptions?.dateRange.maxFinish;

      filters['startFinish'] = hasChangedStartDate ? startFinish : undefined;
      filters['endFinish'] = hasChangedEndDate ? endFinish : undefined;

      updateFilterOptions(filters);
    },
    filterType: 'range' as const,
  };

  const regionFilter = {
    icon: reg.region.icon,
    title: t(reg.region.titleKey),
    key: ['continent', 'politicalRegion'],
    placeholder: t('poiExperience.filters.placeholder.region'),
    keyOptions: [
      { label: t('poiExperience.filters.options.all'), value: '' },
      ...(poiFilterDataOptions?.continents ?? []).map(c => ({
        label: c,
        value: c,
      })),
      ...(poiFilterDataOptions?.politicalRegions ?? []).map(r => ({
        label: r,
        value: r,
      })),
    ],
    onUpdate: (props: Record<string, string | undefined>) => {
      const updatedValue = Object.values(props)[0];

      if (!updatedValue) {
        updateFilterOptions({
          continent: undefined,
          politicalRegion: undefined,
        });
        return;
      }

      if (poiFilterDataOptions?.continents.includes(updatedValue)) {
        updateFilterOptions({
          continent: updatedValue,
          politicalRegion: undefined,
        });
      }

      if (poiFilterDataOptions?.politicalRegions.includes(updatedValue)) {
        updateFilterOptions({
          continent: undefined,
          politicalRegion: updatedValue,
        });
      }
    },
    filterType: 'select' as const,
  };

  const filterConfig: FilterConfig = [
    [
      ...(isDesktop ? [] : [designFilterRow]),
      statusFilter,
      buildYesNoFilter('smokersNeverEligible'),
      buildYesNoFilter('smokingCessationOffered'),
      sexFilter,
      buildYesNoFilter('targetOutreach'),
      buildYesNoFilter('useCade'),
      buildYesNoFilter('riskModellingUsed'),
      buildYesNoFilter('biomarkersCollected'),
      buildYesNoFilter('resultsAvailable'),
      dateRangeFilter,
      regionFilter,
    ],
  ];

  if (selectedNavigationItem !== 'filter') {
    return null;
  }

  return (
    <Filter
      resetFilterOptions={resetFilterOptions}
      filterConfig={filterConfig}
    />
  );
}
